import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiV2 } from "api/firebase";
import { RequestKey } from "api/request-key";
import {
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";

interface UseNotificationBarStatusResult {
  isUnreadNotification: boolean;
  isNewNotifications: boolean;
  refetch: () => Promise<unknown>;
}

/** Notification-bell status for the AppBar, kept live via the `UPDATED_NOTIFICATION_BAR` subscription. */
export const useNotificationBarStatus = (
  showId: string,
  // Unused; the count is scoped by `showId` only.
  organiserId?: string
): UseNotificationBarStatusResult => {
  const { data, refetch } = useQuery({
    queryKey: [RequestKey.GetNotificationBarCount, showId],
    queryFn: () => apiV2.notificationBar.getCount({ showId }),
    enabled: !!showId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const isUnreadNotification = Boolean(data?.unread);
  const isNewNotifications = Boolean(data?.opened) || Boolean(data?.unread);

  useEffect(() => {
    if (!showId) return undefined;

    const listenerId = "count_unread_message_notification_bars";

    apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
      key: NotificationType.UPDATED_NOTIFICATION_BAR,
      showId,
      listenerId,
      callback: async (item) => {
        if (item.showId === showId) {
          await refetch();
        }
      },
    });

    return () => {
      apiV2.notification.unsubscribe({
        key: NotificationType.UPDATED_NOTIFICATION_BAR,
        showId,
        listenerId,
      });
    };
  }, [showId, organiserId, refetch]);

  return {
    isUnreadNotification,
    isNewNotifications,
    refetch,
  };
};
